import {
  ListItem,
  Box,
  ListItemText,
  Divider,
  Chip,
  Popover,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  getIntlTranslation,
  getTranslationVariantText,
  getVariantText,
} from "../../../../../utils/helper";
import { getBorderColor } from "./BuildingProfileContainer";
import { useBuildingProfileContextHook } from "./BuildingProfileContext";
import { useSelector } from "react-redux";
import RenderInputType from "./RenderInputType";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";
import EditCustomPropertyLabel from "../../../common/input-types/EditCustomPropertyLabel";
import CustomIconButton from "../../../../../components/buttons/icons-buttons/CustomIconButton";

export const RenderPropertyNode = () => {
  const scope = useSelector((state) => state.container.scope);
  const { itemList, properties, setProperties, setItemList, currentSubAsset } =
    useBuildingProfileContextHook();
  const [propertyList, setPropertyList] = useState(itemList);
  const [propertyToEdit, setPropertyToEdit] = useState({});
  const [openPopOver, setOpenPopOver] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [updatedPropertyList, setUpdatedPropertyList] = useState([]);

  // Helper function to check if property matches any of the given keywords
  const propertyMatchesKeywords = (property, keywords) => {
    return keywords.some(keyword =>
      property.nodeName?.toUpperCase().includes(keyword.toUpperCase()) ||
      property.labelTranslation?.some(label =>
        label.title?.toUpperCase().includes(keyword.toUpperCase())
      )
    );
  };

  // Define 11 common fields (show for ALL asset types)
  const commonFields = [
    'LOCATION', 'ADRESSE', 'STANDORT', 'LAGE', 'ORT',
    'CONSTRUCTION YEAR', 'BAUJAHR', 'ERRICHTUNGSJAHR',
    'TYPE OF USE', 'NUTZUNGSART', 'NUTZUNG', 'VERWENDUNG',
    'PLOT AREA', 'GRUNDSTÜCKSFLÄCHE', 'GRUNDFLÄCHE',
    'GROSS FLOOR AREA', 'BRUTTOGRUNDFLÄCHE', 'BGF',
    'RENTAL AREA', 'MIETFLÄCHE',
    'TOTAL FLOORS', 'STOCKWERKE INSG', 'GESAMTSTOCKWERKE',
    'FLOORS ABOVE GROUND', 'STOCKWERKE OBERIRDISCH', 'OBERIRDISCHE STOCKWERKE',
    'FLOORS UNDERGROUND', 'STOCKWERKE UNTERIRDISCH', 'UNTERIRDISCHE STOCKWERKE',
    'TOTAL CAR PARKING SPACES', 'GESAMTE ANZAHL PARKPLÄTZE', 'PARKPLÄTZE GESAMT',
    'PARKING SPACES WITH E-CHARGING STATION', 'STELLPLÄTZE MIT E-LADESTATION', 'E-LADESTATION'
  ];

  // Define special fields (show only for specific asset types)
  const specialFieldsByType = {
    'Wohnen': [
      'NUMBER OF FLATS', 'ANZAHL DER WOHNUNGEN', 'ANZAHL WOHNUNGEN', 'WOHNUNGSANZAHL'
    ],
    'Logistik': [
      'FLOOR LOAD CAPACITY', 'TRAGFÄHIGKEIT BODEN', 'BODENBELASTUNG',
      'CLEAR HEIGHT', 'LICHTE HÖHE', 'DECKENHÖHE', 'RAUMHÖHE'
    ],
    'Hotel': [
      'NUMBER OF GUESTROOMS', 'ANZAHL GÄSTEZIMMER', 'ZIMMERANZAHL', 'GÄSTERÄUME'
    ]
  };

  // Check if a property should be shown based on subAssetTypes
  const shouldShowProperty = (property, allSpecialFields = []) => {
    // Always hide SHORT NAME
    const isShortName = property.nodeName?.toUpperCase().includes('SHORT NAME') ||
                        property.nodeName?.toUpperCase().includes('KURZBEZEICHNUNG') ||
                        property.nodeName?.toUpperCase().includes('KURZE BEZEICHNUNG') ||
                        property.labelTranslation?.some(label =>
                          label.title?.toUpperCase().includes('SHORT NAME') ||
                          label.title?.toUpperCase().includes('KURZBEZEICHNUNG') ||
                          label.title?.toUpperCase().includes('KURZE BEZEICHNUNG')
                        );
    if (isShortName) {
      return false;
    }

    // Check if it's a common field (show for all)
    if (propertyMatchesKeywords(property, commonFields)) {
      return true;
    }

    // Check if it's a special field for ANY of the asset types
    if (allSpecialFields.length > 0 && propertyMatchesKeywords(property, allSpecialFields)) {
      return true;
    }

    // Hide everything else
    return false;
  };

  // Filter properties based on subAssetType
  const filterPropertiesBySubAssetType = (itemList, subAssetTypeParam) => {
    // Map English asset type names to German (for special fields lookup)
    const assetTypeMapping = {
      'Logistic': 'Logistik',
      'Logistics': 'Logistik',
      'Residential': 'Wohnen',
      'Hotel': 'Hotel'
    };

    // Collect ALL sub-asset types (for multi-sub-asset support)
    const subAssetTypes = [];

    // Try getting from subAssetList (may have multiple sub-assets)
    if (currentSubAsset?.subAssetList && currentSubAsset.subAssetList.length > 0) {
      currentSubAsset.subAssetList.forEach(item => {
        const mappedType = assetTypeMapping[item.value] || item.value;
        if (mappedType && !subAssetTypes.includes(mappedType)) {
          subAssetTypes.push(mappedType);
        }
      });
    }

    // Also try getting from subAssetTypeParam
    if (subAssetTypeParam) {
      const mappedType = assetTypeMapping[subAssetTypeParam] || subAssetTypeParam;
      if (!subAssetTypes.includes(mappedType)) {
        subAssetTypes.push(mappedType);
      }
    }

    // Also try getting from currentSubAsset?.subAssetType
    if (currentSubAsset?.subAssetType) {
      const mappedType = assetTypeMapping[currentSubAsset.subAssetType] || currentSubAsset.subAssetType;
      if (!subAssetTypes.includes(mappedType)) {
        subAssetTypes.push(mappedType);
      }
    }

    // Collect ALL special fields from ALL sub-asset types
    const allSpecialFields = [];
    subAssetTypes.forEach(subAssetType => {
      if (specialFieldsByType[subAssetType]) {
        allSpecialFields.push(...specialFieldsByType[subAssetType]);
      }
    });

    // Filter each property category
    const filteredInputFields = itemList.inputFields?.filter(property =>
      shouldShowProperty(property, allSpecialFields)
    ) || [];

    const filteredInputAreaFields = itemList.inputAreaFields?.filter(property =>
      shouldShowProperty(property, allSpecialFields)
    ) || [];

    const filteredInputParkingFields = itemList.inputParkingFields?.filter(property =>
      shouldShowProperty(property, allSpecialFields)
    ) || [];

    const filteredInputFloorFields = itemList.inputFloorFields?.filter(property =>
      shouldShowProperty(property, allSpecialFields)
    ) || [];

    return {
      ...itemList,
      inputFields: filteredInputFields,
      inputAreaFields: filteredInputAreaFields,
      inputParkingFields: filteredInputParkingFields,
      inputFloorFields: filteredInputFloorFields
    };
  };

useEffect(() => {
  const filteredItemList = filterPropertiesBySubAssetType(itemList, currentSubAsset?.subAssetType);
  setPropertyList(filteredItemList);
}, [itemList, currentSubAsset?.subAssetType, JSON.stringify(currentSubAsset?.subAssetList)])

  const getPropertyName = (property) => {
    const inputObj = property?.labelTranslation.find(
      (e) => e.language === scope.projectLanguageCode
    );
    return (
      <>
        {getVariantText(inputObj?.title || property?.name, "caption", {
          fontWeight: "bold",
          color: "#254a9a",
          margin: 5,
          textTransform: "uppercase",
        })}
      </>
    );
  };

  /*{property.custom && getCustomOptions(property)}*/

  const createListItemRow = (property, index) => {
    return (
      <>
      {properties?.has(property.nodeName) ?
        (
        <ListItem
          key={`item-${property.nodeIdent}`}
          style={{
            margin: "4px 0px",
            borderRadius: 4,
            borderLeft: getBorderColor(properties?.get(property.nodeName)),
            backgroundColor: properties.get(property.nodeName).updated
              ? "#ff2a5130"
              : "#ffffff",
          }}
          className="d-flex flex-row flex-wrap w-100 p-1"
        >
          <Box className="d-flex align-items-center w-25 p-1">
            <ListItemText
              primary={
                <>
                  <div
                    className={"d-flex align-items-center property-name"}
                    id={property.nodeIdent}
                  >
                    {getPropertyName(property)}
                  </div>
                </>
              }
            />
            {property.custom && renderCustomAnchor(property)}
          </Box>
          <Box className="w-75 p-1">
            <RenderInputType item={property} />
          </Box>
        </ListItem>) :
        <></>
            }
      </>
    );
  };

  const onPropertyEdit = (event, property) => {
    setOpenPopOver(true);
    setAnchorEl(event.currentTarget);
    setPropertyToEdit(property);
  }

  const onPropertyEditClose = () => {
    setAnchorEl(null);
    setOpenPopOver(false);
  };

  const updateItemList = (val, tempArray) => {
    setItemList((prev) => {
      const newItems = {...prev};
      if (
        val.nodeType === "area" ||
        val.nodeType.includes("-area")
      ) {
        newItems.inputAreaFields = [...tempArray];
      } else if (val.nodeType.includes("parking")) {
        newItems.inputParkingFields = [...tempArray];
      } else if (val.nodeType.includes("floor")) {
        newItems.inputFloorFields = [...tempArray];
      } else {
        newItems.inputFields = [...tempArray];
      }
      return newItems;
    })
  }

  function updateAndReplaceObjectInArray(lookupArray, selectedProperty, val) {
    let translation = val.labelTranslation.find(
      (e) => e.language === scope.projectLanguageCode
    );
    const index = lookupArray.findIndex(
      (item) => item.nodeName === selectedProperty.nodeName
    );

    if (index !== -1) {
      const updatedObject = { ...lookupArray[index], deleted: true };
      if (
        selectedProperty.isTemporary &&
        val.nodeName !== selectedProperty.nodeName
      ) {
        lookupArray[index] = val;
        const oldObject = properties.get(selectedProperty.nodeName);
        setProperties(prevProperties => {
          const newProperties = new Map(prevProperties);
          newProperties.set(val.nodeName,{
            ...oldObject,
            nodeName: val.nodeName,
            deleted: val.deleted,
            updated: val.updated,
            labelTranslation : translation,
          });
          newProperties.delete(oldObject.nodeName);
          return newProperties;
        });
      } else {
        lookupArray[index] = val;
        lookupArray.push(updatedObject);
        const oldObject = properties.get(selectedProperty.nodeName);
        setProperties(prevProperties => {
          const newProperties = new Map(prevProperties);
          newProperties.set(val.nodeName,{
            ...oldObject,
            nodeName: val.nodeName,
            deleted: val.deleted,
            updated: val.updated,
            labelTranslation : translation,
          });
          newProperties.get(selectedProperty.nodeName).deleted = true;
          return newProperties;
        });
      }
    }
    return [...lookupArray];
  }

  const updateProperty = (val, propertyToEdit) => {
    let tempArray = [];
    if (val.nodeType === "area" || val.nodeType.includes("-area")) {
      tempArray = [...propertyList.inputAreaFields];
    } else if (val.nodeType.includes("parking")) {
      tempArray = [...propertyList.inputParkingFields];
    } else if (val.nodeType.includes("floor")) {
      tempArray = [...propertyList.inputFloorFields];
    } else {
      tempArray = [...propertyList.inputFields];
    }

    let updatedArr = updateAndReplaceObjectInArray(
      tempArray,
      propertyToEdit,
      val
    );

    updateItemList(val, updatedArr);
  }

  const renderCustomAnchor = (property) => {
    return (
      property.custom && (
        <>
          <CustomIconButton
            id={`menu_button_${property.nodeName}`}
            icon={
              <ArrowDropDownIcon
                style={{
                  color: "#d08f8e",
                  fontSize: 20,
                }}
              />
            }
            permission={null}
            handleClick={(e) => onPropertyEdit(e, property)}
            tooltip={{
              title: getIntlTranslation("tooltip.edit"),
            }}
            style={{
              borderRadius: 0,
            }}
            aria-describedby={`prop_menu_${property.nodeName}`}
          />
          <Popover
            id={`prop_menu_${property.nodeName}`}
            open={openPopOver}
            anchorEl={anchorEl}
            onClose={(e) => onPropertyEditClose(e)}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
          >
            <EditCustomPropertyLabel
              onClose={() => onPropertyEditClose()}
              property={propertyToEdit}
              handleSave={(val) => {
                updateProperty(val, propertyToEdit);
                onPropertyEditClose();
              }}
              onDelete={(val) => {
                let tempArray = [...propertyList.inputFields];
                if (val.nodeType === "area" || val.nodeType.includes("-area")) {
                  tempArray = [...propertyList.inputAreaFields];
                } else if (val.nodeType.includes("parking")) {
                  tempArray = [...propertyList.inputParkingFields];
                } else if (val.nodeType.includes("floor")) {
                  tempArray = [...propertyList.inputFloorFields];
                }
                let findIndex = tempArray.findIndex(
                  (f) => f.nodeName === val.nodeName
                );
                if (findIndex !== -1) {
                  tempArray[findIndex] = {
                    ...tempArray[findIndex],
                    deleted: true,
                  };

                  setProperties(prevProperties => {
                    const newProperties = new Map(prevProperties);
                    newProperties.get(val.nodeName).updated = true;
                    return newProperties;
                  });

                  updateItemList(val, tempArray);
                 
                }
                onPropertyEditClose();
              }}
            />
          </Popover>
        </>
      )
    );
  };

  const getDivider = (label) => {
    return [
      <>
        <Chip
          textAlign="left"
          className="g-btn-secondary"
          style={{ borderRadius: 4, margin: 4, width: "98%" }}
          label={getTranslationVariantText(label, "body2", {
            fontWeight: "bold",
          })}
        />
      </>,
    ];
  };

  const renderGroupedFiles = (fields) => {
    // Sort fields by database order
    const sortedFields = fields.toSorted((a, b) => (a.nodeOrder || a.order || 999) - (b.nodeOrder || b.order || 999));

    return sortedFields.map((el, index) => {
      if (el.hidden || el.deleted) {
        return null;
      }
      return createListItemRow(el, index);
    });
  };

  const renderProperties = () => {
    let temp = [];

    // Sort inputFields by database order
    const sortedInputFields = propertyList?.inputFields.toSorted((a, b) => (a.nodeOrder || a.order || 999) - (b.nodeOrder || b.order || 999));

    let inputFieldsRender = sortedInputFields.map((el, index) => {
      if (el.hidden || el.deleted) {
        return null;
      }
      return createListItemRow(el, index);
    });

    temp = [...inputFieldsRender];

    if (propertyList?.inputFloorFields.length > 0) {
      let floorDivider = getDivider("_floor-applicable");
      let floorRender = renderGroupedFiles(propertyList.inputFloorFields);
      temp = [...temp, ...floorDivider, ...floorRender];
    }
    if (propertyList?.inputAreaFields.length > 0) {
      let areaDivider = getDivider("_area-applicable");
      let areaRender = renderGroupedFiles(propertyList.inputAreaFields);
      temp = [...temp, ...areaDivider, ...areaRender];
    }
    if (propertyList?.inputParkingFields.length > 0) {
      let parkingDivider = getDivider("_parking-applicable");
      let parkingRender = renderGroupedFiles(propertyList.inputParkingFields);
      temp = [...temp, ...parkingDivider, ...parkingRender];
    }
    return temp;
  };

  return <>{!!propertyList && renderProperties()}</>;
};
